/**
 * CrossPortalService.ts
 * 
 * Service per la gestione delle comunicazioni cross-portal nel portale Ufficio Stile
 * Gestisce commenti e allegati globali/interni attraverso il CrossPortalOrchestrator
 */

import { apiClient } from "./api";

export interface CrossPortalCommentDto {
  // Support both legacy and new field names
  commentId?: string; // canonical id
  id?: string; // legacy alias
  crossPortalRequestId?: number; // canonical cross-portal request id
  requestId?: number; // legacy alias
  content: string;
  authorName?: string; // canonical
  createdBy?: string; // legacy alias
  authorUsername?: string;
  authorPortal?: string; // canonical
  sourcePortal?: string; // legacy alias
  createdAt: string;
  isGlobal: boolean;
  commentType?: string;
  parentCommentId?: string;
  threadLevel?: number;
}

export interface AddGlobalCommentDto {
  // Accept both canonical and legacy field names
  crossPortalRequestId?: number; // canonical
  requestId?: number; // legacy
  content: string;
  authorName?: string; // canonical
  createdBy?: string; // legacy
  authorUsername?: string;
  authorPortal?: string; // canonical
  sourcePortal?: string; // legacy
  commentType?: string;
  parentCommentId?: string;
  threadLevel?: number;
}

export interface CrossPortalAttachmentDto {
  id?: number;
  attachmentId?: string; // id
  crossPortalRequestId: number; // requestId come int
  fileName: string;
  contentType: string; // mimeType
  fileSizeBytes: number; // fileSize
  attachmentType?: string;
  uploaderName: string; // createdBy
  uploaderUsername?: string;
  uploadedByPortal: string; // sourcePortal (legacy alias)
  sourcePortal?: string; // campo dal backend MergedAttachmentDto
  uploadedAt: string; // createdAt
  isGlobal: boolean;
  azureBlobUrl?: string;
  description?: string;
}

export interface UploadGlobalAttachmentDto {
  file: File;
  crossPortalRequestId?: number; // canonical
  requestId?: number; // legacy
  ufficioStileRequestId?: number; // canonical
  uploaderName?: string; // canonical
  createdBy?: string; // legacy
  uploaderUsername?: string;
  uploadedByPortal?: string; // canonical
  sourcePortal?: string; // legacy
  attachmentType?: string;
  description?: string;
  isGlobal?: boolean;
}

export interface AttachmentDownloadDto {
  url: string;
  fileName: string;
}

class CrossPortalService {
  // Gestione Commenti Globali
  async getGlobalComments(crossPortalRequestId: number): Promise<CrossPortalCommentDto[]> {
    const response = await apiClient.get<CrossPortalCommentDto[]>(
      `/api/cross-portal/global-comments/${crossPortalRequestId}`
    );
    return response.data;
  }

  async addGlobalComment(payload: AddGlobalCommentDto): Promise<CrossPortalCommentDto> {
    const response = await apiClient.post<CrossPortalCommentDto>('/api/cross-portal/receive-global-comment', payload);
    return response.data;
  }

  async addInternalComment(payload: AddGlobalCommentDto): Promise<CrossPortalCommentDto> {
    const response = await apiClient.post<CrossPortalCommentDto>('/api/internal/ufficio-stile/comments', payload);
    return response.data;
  }

  // Gestione Allegati Globali
  // Nota: il parametro è l'ID della richiesta in Ufficio Stile (ufficioStileRequestId)
  // Il backend recupererà automaticamente anche il crossPortalRequestId dalla richiesta
  async getGlobalAttachments(requestId: number): Promise<CrossPortalAttachmentDto[]> {
    // apiClient.get<T>() returns ApiResponse<T> = { data: T, success: boolean, ... }
    // Il backend restituisce { data: [...], success: true }
    const response = await apiClient.get<CrossPortalAttachmentDto[]>(
      `/api/cross-portal/global-attachments/${requestId}`
    );
    return response.data || [];
  }

  async getMergedAttachments(crossPortalRequestId: number): Promise<CrossPortalAttachmentDto[]> {
    const response = await apiClient.get<CrossPortalAttachmentDto[]>(
      `/api/cross-portal/request/${crossPortalRequestId}/merged-attachments`
    );
    return response.data;
  }

  async uploadGlobalAttachment(payload: UploadGlobalAttachmentDto): Promise<CrossPortalAttachmentDto> {
    const formData = new FormData();
    formData.append('File', payload.file); // Campo corretto: File
    if (payload.crossPortalRequestId !== undefined) {
      formData.append('CrossPortalRequestId', payload.crossPortalRequestId.toString());
    } else if (payload.requestId !== undefined) {
      formData.append('CrossPortalRequestId', payload.requestId.toString());
    }

    if (payload.ufficioStileRequestId !== undefined) {
      formData.append('UfficioStileRequestId', payload.ufficioStileRequestId.toString());
    }

    const uploader = payload.uploaderName ?? payload.createdBy;
    if (uploader) {
      formData.append('UploadedBy', uploader);
      formData.append('UploaderName', uploader);
    }

    if (payload.description) {
      formData.append('Description', payload.description);
    }

    const sourcePortal = payload.uploadedByPortal ?? payload.sourcePortal;
    if (sourcePortal) {
      formData.append('SourcePortal', sourcePortal);
    }

    // isGlobal: se non specificato, default a true (per allegati globali)
    // isGlobal: false solo se esplicitamente impostato (per allegati locali)
    formData.append('IsGlobal', payload.isGlobal !== false ? 'true' : 'false');

    const response = await apiClient.upload<CrossPortalAttachmentDto>('/api/cross-portal/upload-global-attachment', formData);
    return response.data;
  }

  async getAttachmentDownloadUrl(attachmentId: string, fallbackFileName?: string): Promise<AttachmentDownloadDto> {
    // Return the API endpoint URL directly - the backend will handle the redirect
    const apiUrl = `${import.meta.env.VITE_API_BASE_URL}/api/cross-portal/download-global-attachment/${attachmentId}`;
    
    return {
      url: apiUrl,
      fileName: fallbackFileName || 'attachment'
    };
  }

  async deleteGlobalAttachment(id: string): Promise<void> {
    await apiClient.delete(`/api/cross-portal/delete-global-attachment/${id}`);
  }

  // Metodo per ottenere allegati locali (non cross-portal)
  async getLocalAttachments(requestId: string): Promise<CrossPortalAttachmentDto[]> {
    const response = await apiClient.get<CrossPortalAttachmentDto[]>(
      `/attachment/by-request/${requestId}`
    );
    return response.data;
  }
}

export const crossPortalService = new CrossPortalService();
export default crossPortalService;
